/**
 * TokenService — Single source of truth for Auth0 access tokens.
 *
 * The Auth0 SDK is the owner of the token. This service holds a reference
 * to Auth0's `getAccessTokenSilently` and exposes a single `getToken()`
 * method that all callers use (API client, auth hooks, etc.).
 *
 * No token is stored in localStorage by the app — Auth0 manages its own
 * localStorage cache internally.
 *
 * Bridge sessions: When a mobile-to-web session handoff is in progress,
 * an override token is stored both in-memory AND in sessionStorage so it
 * survives React StrictMode's simulate-unmount/remount cycle and SPA
 * navigations that don't cause a full page reload.
 */

type GetTokenSilentlyFn = (options?: Record<string, unknown>) => Promise<string>;

const BRIDGE_TOKEN_KEY = 'tilly_bridge_token';

let _getTokenSilently: GetTokenSilentlyFn | null = null;
let _overrideToken: string | null = null;

/** Read bridge token from sessionStorage (survives React StrictMode cleanup). */
function _readSessionBridgeToken(): string | null {
  try {
    return window.sessionStorage.getItem(BRIDGE_TOKEN_KEY);
  } catch {
    return null;
  }
}

export const tokenService = {
  /**
   * Register Auth0's `getAccessTokenSilently`.
   * Called once from the `TokenServiceRegistrar` inside `AuthProvider`
   * after the Auth0 context is available.
   */
  register(fn: GetTokenSilentlyFn): void {
    _getTokenSilently = fn;
  },

  /**
   * Release the registered Auth0 function.
   * Called on AuthProvider unmount — intentionally does NOT clear the bridge
   * session token because AuthProvider cleanup fires during React StrictMode's
   * simulate-unmount, which happens before BridgePage's async exchange
   * completes. The bridge token is managed separately.
   */
  clear(): void {
    _getTokenSilently = null;
  },

  /**
   * Set an override token (used for session bridge from mobile).
   * This token takes precedence over the Auth0 SDK token.
   * Persisted to sessionStorage so it survives React StrictMode's cleanup.
   */
  setOverrideToken(token: string): void {
    _overrideToken = token;
    try {
      window.sessionStorage.setItem(BRIDGE_TOKEN_KEY, token);
    } catch {
      // sessionStorage unavailable (e.g., private mode restrictions) — no-op.
    }
  },

  /** Clear the override token and remove it from sessionStorage. */
  clearOverrideToken(): void {
    _overrideToken = null;
    try {
      window.sessionStorage.removeItem(BRIDGE_TOKEN_KEY);
    } catch {
      // ignore
    }
  },

  /** Returns true when a bridge session override token is active. */
  hasOverrideToken(): boolean {
    return (_overrideToken ?? _readSessionBridgeToken()) !== null;
  },

  /**
   * Get a valid access token.
   *
   * Priority:
   * 1. In-memory override token (bridge session)
   * 2. sessionStorage bridge token (bridge session, persisted across re-renders)
   * 3. Auth0's `getAccessTokenSilently`
   *
   * Returns `null` when called before Auth0Provider has mounted (safe —
   * the API client will simply omit the Authorization header, causing a
   * 401 which triggers the unauthorized handler).
   */
  async getToken(options?: { forceRefresh?: boolean }): Promise<string | null> {
    // Override token takes precedence (from session bridge).
    const bridgeToken = _overrideToken ?? _readSessionBridgeToken();
    if (bridgeToken) {
      // Sync in-memory from sessionStorage on first call after StrictMode cleanup.
      if (!_overrideToken) _overrideToken = bridgeToken;
      return bridgeToken;
    }

    if (!_getTokenSilently) return null;
    return _getTokenSilently(options?.forceRefresh ? { cacheMode: 'off' } : undefined);
  },
};
